package songwritersnotebook.app

import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.await
import dev.gitlive.firebase.functions.*
import kotlinx.browser.window
import androidx.compose.runtime.*
import androidx.navigation.NavHostController
import songwritersnotebook.app.navigation.AppRoutes

class WebPlatform : Platform {
    override val name: String = "Web"
    override val deviceName: String = "Web Browser"
}

actual class JsHttpsCallableResult actual constructor(val rawData: Any?) {
    actual fun data(): Any? {
        return rawData.asDynamic().data
    }
}

actual fun getPlatform(): Platform = WebPlatform()

actual val ioDispatcher: CoroutineDispatcher = Dispatchers.Default

actual fun ByteArray.toFirebaseData(): dev.gitlive.firebase.storage.Data {
    val uint8Array = org.khronos.webgl.Uint8Array(this.size)
    for (i in indices) {
        uint8Array.asDynamic()[i] = this[i]
    }
    return dev.gitlive.firebase.storage.Data(uint8Array)
}

actual fun parseJsonToNative(jsonString: String): Any? {
    return kotlin.js.JSON.parse(jsonString)
}

actual suspend fun callIfJs(name: String, callable: Any, data: Any?, authToken: String?): Any? {
    // Parse the data if it's a JSON string so it becomes a native JS object
    val finalData = if (data is String) kotlin.js.JSON.parse<Any?>(data) else data
    // Directly use the global helper we moved to app-bridge.js
    val result = jsResultPromiseAwait(js("window.snBridge.callFirebaseFunctionSafe(name, finalData, null)")) ?: return null
    return JsHttpsCallableResult(result)
}

actual suspend fun jsResultPromiseAwait(promise: Any?): Any? {
    if (promise == null) return null
    return promise.unsafeCast<kotlin.js.Promise<Any?>>().await()
}

actual fun <T> Any?.jsUnsafeCast(): T = this.unsafeCast<T>()

@Composable
actual fun SyncWebUrl(navController: NavHostController) {
    LaunchedEffect(navController) {
        // Initial sync: Start at the current URL path
        val currentPath = window.location.pathname
        val normalizedPath = currentPath.removePrefix("/launch")
        println("DEBUG_NAV: Initial sync path: $currentPath (normalized: $normalizedPath)")
        if (normalizedPath != "/" && normalizedPath.isNotEmpty()) {
            navController.navigate(normalizedPath) {
                popUpTo(AppRoutes.AUTH_SCREEN) { inclusive = true }
            }
        }

        // Listen for Nav changes and update browser history
        navController.currentBackStackEntryFlow.collect { entry ->
            val pattern = entry.destination.route ?: return@collect
            
            // Reconstruct the actual path with arguments
            var actualPath = pattern
            
            // Manually handle known parameters for this app
            val songId = entry.arguments?.getString("songId")
            if (!songId.isNullOrEmpty()) {
                // Handle both with and without leading slash in the replace pattern
                actualPath = pattern.replace("{songId}", songId)
            }
            
            val currentBrowserPath = window.location.pathname
            val expectedBrowserPath = if (actualPath.startsWith("/launch")) actualPath else "/launch$actualPath"
            // Ensure we don't push the same path twice and avoid unreplaced placeholders
            if (expectedBrowserPath != currentBrowserPath && !actualPath.contains("{")) {
                println("DEBUG_NAV: Pushing actual path to history: $expectedBrowserPath")
                window.history.pushState(null, "", expectedBrowserPath)
            }
        }
    }

    // Listen for browser Back/Forward buttons
    DisposableEffect(Unit) {
        val onPopState: (dynamic) -> Unit = { 
            val path = window.location.pathname
            val normalizedPath = path.removePrefix("/launch")
            val navPath = if (normalizedPath.isEmpty() || normalizedPath == "/") AppRoutes.HOME_SCREEN else normalizedPath
            navController.navigate(navPath) {
                launchSingleTop = true
            }
        }
        window.addEventListener("popstate", onPopState)
        onDispose {
            window.removeEventListener("popstate", onPopState)
        }
    }
}

actual fun extractMapFromJsResult(result: Any?): Map<String, Boolean> {
    if (result == null) return emptyMap()
    val resData = result.unsafeCast<JsHttpsCallableResult>().data().asDynamic()
    val resultMap = mutableMapOf<String, Boolean>()
    
    // Iterate over JS object keys
    val keys = js("Object.keys(resData)")
    val size = keys.length as Int
    for (i in 0 until size) {
        val key = keys[i].toString()
        val value = resData[key] as Boolean
        resultMap[key] = value
    }
    return resultMap
}

actual fun extractPronunciationsFromJsResult(result: Any?): Map<String, String> {
    if (result == null) return emptyMap()
    val resData = result.unsafeCast<JsHttpsCallableResult>().data().asDynamic()
    val resultMap = mutableMapOf<String, String>()
    
    val keys = js("Object.keys(resData)")
    val size = keys.length as Int
    for (i in 0 until size) {
        val key = keys[i].toString()
        val value = resData[key].toString()
        resultMap[key] = value
    }
    return resultMap
}

actual fun extractUrlFromJsResult(result: Any?): String? {
    if (result == null) return null
    val resData = result.unsafeCast<JsHttpsCallableResult>().data().asDynamic()
    logNativeObjectActual(resData)
    return resData.url.toString()
}

actual fun logNativeObjectActual(obj: Any?) {
    val dyn = obj.asDynamic()
    js("console.log('NATIVE_OBJ_INSPECT:', dyn)")
}

actual suspend fun getAppCheckToken(): String? {
    return try {
        val bridge = js("window.snBridge")
        if (bridge == undefined || bridge.getAppCheckToken == undefined) {
            return null
        }
        val result = jsResultPromiseAwait(bridge.getAppCheckToken())
        result?.toString()
    } catch (e: Exception) {
        null
    } catch (e: dynamic) {
        null
    }
}

